import { Platform } from '@angular/cdk/platform';
import {
  FormatWidth,
  getLocaleDateFormat,
  getLocaleFirstDayOfWeek,
  WeekDay,
  AsyncPipe,
} from '@angular/common';
import {
  Component,
  Injectable,
  Input,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  SimpleChanges,
  output,
  inject,
} from '@angular/core';
import { TranslateService, TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from 'date-fns';
import { NativeDateAdapter } from '@angular/material/core';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { DateV2Parser, ReactiveComponent, IconV2Module } from '@schenck/rotec-components';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatFormField, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepickerToggleIcon,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import {
  MatTimepickerInput,
  MatTimepicker,
  MatTimepickerToggle,
} from '@angular/material/timepicker';
import { FormsModule } from '@angular/forms';

/**
 * DateAdapter for formatting and parsing dates for Angular Material.
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter implements OnDestroy {
  // TODO way more advanced parsing needed:
  // - to support combinations (1M1h)
  public static parseCustomStr(dateStr: string, firstDayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6): Date {
    const date = CustomDateAdapter.getDateForHardcodedValue(dateStr, firstDayOfWeek);
    if (date) {
      return date;
    }

    if (!dateStr.includes('-')) {
      return new Date(NaN);
    }

    const now = new Date().getTime();
    const offset = dateStr.split('-')[1];
    const offsetValue = Number(offset.substr(0, offset.length - 1));
    const offsetUnit = offset[offset.length - 1];
    return new Date(now - offsetValue * CustomDateAdapter.getOffsetMs(offsetUnit));
  }

  public static getOffsetMs(offsetUnit: string): number {
    switch (offsetUnit) {
      case 's':
        return 1000;
      case 'm':
        return 1000 * 60;
      case 'h':
        return 1000 * 60 * 60;
      case 'd':
        return 1000 * 60 * 60 * 24;
      case 'w':
        return 1000 * 60 * 60 * 24 * 7;
      case 'M':
        return 1000 * 60 * 60 * 24 * 31;
      default:
        return NaN;
    }
  }

  public static getDateForHardcodedValue(
    dateStr: string,
    firstDayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6
  ): Date | null {
    dateStr = dateStr.toLowerCase();
    switch (dateStr) {
      case 'now':
        return new Date();
      case 'start of day':
        return startOfDay(new Date());
      case 'end of day':
        return endOfDay(new Date());
      case 'start of week':
        return startOfWeek(new Date(), { weekStartsOn: firstDayOfWeek });
      case 'end of week':
        return endOfWeek(new Date(), { weekStartsOn: firstDayOfWeek });
      case 'start of month':
        return startOfMonth(new Date());
      case 'end of month':
        return endOfMonth(new Date());
      case 'start of year':
        return startOfYear(new Date());
      case 'end of year':
        return endOfYear(new Date());
      default:
        return null;
    }
  }

  private destroy$ = new Subject();
  private dateTimeFormat!: string;
  private firstDayOfWeek!: WeekDay;

  constructor() {
    const translate = inject(TranslateService);
    const platform = inject(Platform);

    super(translate.currentLang, platform);

    translate.onLangChange
      .pipe(startWith(translate.currentLang), takeUntil(this.destroy$))
      .subscribe(() => {
        this.firstDayOfWeek = getLocaleFirstDayOfWeek(translate.currentLang);
        this.setLocale(translate.currentLang);
        this.dateTimeFormat = getLocaleDateFormat(translate.currentLang, FormatWidth.Short);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
  }

  /**
   * Parses the date string.
   *
   * This has to be done by us because Angular Material does not support dynamic parsing at the moment:
   * https://github.com/angular/components/issues/5792
   * https://github.com/angular/components/issues/14291
   */
  public parse(dateStr: string): Date {
    const date = CustomDateAdapter.parseCustomStr(dateStr, this.getFirstDayOfWeek());
    if (!isNaN(date.getTime())) {
      return date;
    }

    return DateV2Parser.parseDateTime(dateStr, this.locale);
  }

  public deserialize(value: any): Date {
    if (typeof value === 'string') {
      return this.parse(value);
    }
    return super.deserialize(value);
  }

  public format(date: Date): string {
    return format(date, this.dateTimeFormat, { weekStartsOn: this.getFirstDayOfWeek() });
  }

  public getFirstDayOfWeek(): WeekDay {
    return this.firstDayOfWeek;
  }
}

@Component({
  selector: 'mc-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatFormField,
    MatInput,
    MatHint,
    TranslatePipe,
    TranslateDirective,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatIcon,
    MatDatepickerToggleIcon,
    IconV2Module,
    MatDatepicker,
    MatTimepickerInput,
    FormsModule,
    MatTimepicker,
    MatTimepickerToggle,
    AsyncPipe,
  ],
})
export class DatetimePickerComponent extends ReactiveComponent {
  private readonly dataAdapter = inject(CustomDateAdapter);
  private readonly translate = inject(TranslateService);

  /**
   * Placeholder to show when no date set.
   */
  @Input() public placeholder = '';
  /**
   * The date value.
   * Either the date of a string which may be a datestring or one the special cases (see parse-method of CustomDateAdapter).
   */
  @Input() public date?: string | Date;

  /**
   * Emits the new date value.
   * Either the date of a string which may be a datestring or one the special cases (see parse-method of CustomDateAdapter).
   */
  public readonly dateChange = output<string | Date>();

  /**
   * Emits when the datepicker or timepicker changes the date.
   *
   * The timepicker is enabled when the date is a Date object and
   * disabled when the date is a "custom" date string (e.g. "now", "start of day", etc.).
   */
  public enableTimepicker$ = new BehaviorSubject<boolean>(false);

  /**
   * Placeholder for the timepicker input.
   */
  public timepickerPlaceholder$ = this.enableTimepicker$.pipe(
    takeUntil(this.onDestroy$),
    map((enableTimepicker) => (enableTimepicker ? this.translate.instant('Pick a time') : ''))
  );

  public get stringifiedDate(): string {
    return this.date instanceof Date
      ? this.dataAdapter.format(this.date)
      : this.date
        ? this.translate.instant(this.date)
        : this.date;
  }

  public hasError = false;

  /**
   * Handles the date change event from the datepicker or timepicker.
   * @param event
   */
  public onDateChange(event): void {
    this.enableTimepicker$.next(event.value instanceof Date);
    this.dateChange.emit(event.value);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    this.hasError = this.hasDateError(this.date);

    if (changes.date) {
      this.enableTimepicker$.next(this.date instanceof Date);
    }
  }

  /**
   * Handles the date input from the user.
   * @param date
   */
  public onDateInput(date: Date): void {
    this.date = date;
    this.hasError = this.hasDateError(date);
    if (!this.hasError) {
      this.dateChange.emit(date);
    }
  }

  private hasDateError(date: string | Date): boolean {
    const parsed = this.dataAdapter.deserialize(date);
    return parsed ? isNaN(parsed.getTime()) : false;
  }
}
